import React, { useMemo, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import {
    ModuleDashboard,
    ParamTypes,
} from "../../../../../interfaces/Dashboard";

import { PRLockStatus } from "../../../../../interfaces/PedagogicalResourcesManagement";
import TableLegend from "./TableLegend/TableLegend";
import dashboardMessages from "../../dashboardMessages";
import ThContent from "./ThContent/ThContent";
import Avatar from "../../../../../components/Avatar/Avatar";
import InitialTestCell from "./InitialTestCell/InitialTestCell";
import ObjectiveCell from "./ObjectiveCell/ObjectiveCell";

import { useData } from "../../../../../hooks/useData";
import { getObjectiveById } from "../../../../../utils/dataRetrieval";
import { useBreakpoints } from "@evidenceb/athena-common/helpers/breakpoints";
import { VisuallyHidden } from "@evidenceb/athena-common/ui/VisuallyHidden";
import { Icon } from "@evidenceb/athena-common/design-system/Icon";
import SimpleBar from "simplebar-react";
import { useDashboardRouter } from "../../routing";
import cn from "classnames";

import "./ProgressionTable.scss";

interface TableProps {
    students: ModuleDashboard["students"];
    objectives: string[];
    prLockStatus?: PRLockStatus;
    moduleId: string;
}

const ProgressionTable = ({
    students,
    objectives,
    prLockStatus,
    moduleId,
}: TableProps) => {
    const data = useData();
    const intl = useIntl();

    const { isTabletOrSmaller } = useBreakpoints();
    const { classroomId } = useParams<ParamTypes>();
    const { studentDetails: getStudentDetailsUrl } = useDashboardRouter();
    const ref = useRef<HTMLUListElement>(null);

    const hasTestResultColumn = useMemo(
        () => Object.values(students).some((student) => student.testsResults),
        [students]
    );

    const sortByName = (a: string, b: string) => {
        return students[a].lastname.localeCompare(students[b].lastname);
    };

    const dataObjectives = objectives.map((o) => getObjectiveById(o, data));
    const lastObjectiveIndex = objectives.length - 1;

    return (
        <div
            className="progression-table"
            style={{
                marginBottom: isTabletOrSmaller ? 0 : ref.current?.offsetHeight,
            }}
        >
            <TableLegend ref={ref} />
            <div className="objective-table">
                <SimpleBar autoHide={false}>
                    <table
                        summary={intl.formatMessage(
                            messages.progressionTableSummary
                        )}
                    >
                        <thead>
                            <tr>
                                <th id="th-1">
                                    <VisuallyHidden>
                                        {intl.formatMessage(
                                            messages.progressionTableStudentName
                                        )}
                                    </VisuallyHidden>
                                </th>
                                {hasTestResultColumn && (
                                    <th id="th-2">
                                        <span>
                                            {intl.formatMessage(
                                                dashboardMessages.diagTest
                                            )}
                                        </span>
                                    </th>
                                )}

                                {objectives.map((objectiveId, i) => (
                                    <ThContent
                                        key={"th-" + objectiveId}
                                        objectiveId={objectiveId}
                                        index={i}
                                        prLockStatus={prLockStatus}
                                        moduleId={moduleId}
                                        testResultColumn={hasTestResultColumn}
                                    />
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {Object.keys(students)
                                .sort(sortByName)
                                .map((studentId) => (
                                    <tr key={studentId}>
                                        <td headers="th-1">
                                            <Link
                                                to={getStudentDetailsUrl(
                                                    studentId,
                                                    classroomId,
                                                    moduleId
                                                )}
                                            >
                                                <p className="name">
                                                    <span className="name__avatar">
                                                        {!isTabletOrSmaller && (
                                                            <Avatar
                                                                aria-hidden={
                                                                    true
                                                                }
                                                                name={`${students[studentId].firstname} ${students[studentId].lastname}`}
                                                            />
                                                        )}
                                                    </span>
                                                    {
                                                        students[studentId]
                                                            .firstname
                                                    }{" "}
                                                    {
                                                        students[studentId]
                                                            .lastname
                                                    }
                                                </p>
                                                <p className="link-to-profile">
                                                    <span>
                                                        {intl.formatMessage(
                                                            messages.progressionSeeProfile
                                                        )}
                                                    </span>
                                                    <Icon path="chevron_right" />
                                                </p>
                                            </Link>
                                        </td>
                                        {hasTestResultColumn && (
                                            <td headers="th-2">
                                                <InitialTestCell
                                                    student={
                                                        students[studentId]
                                                    }
                                                />
                                            </td>
                                        )}

                                        {Array.isArray(
                                            students[studentId].objectives
                                        ) &&
                                            students[studentId].objectives.map(
                                                (objective, index) => {
                                                    return (
                                                        <ObjectiveCell
                                                            className={cn({
                                                                "last-objective-cell":
                                                                    index ==
                                                                    lastObjectiveIndex,
                                                                "first-objective-cell":
                                                                    index == 0,
                                                            })}
                                                            key={
                                                                "td-" +
                                                                objective.id
                                                            }
                                                            objective={
                                                                objective
                                                            }
                                                            dataObjective={
                                                                dataObjectives[
                                                                    index
                                                                ]
                                                            }
                                                            student={
                                                                students[
                                                                    studentId
                                                                ]
                                                            }
                                                            objectiveIndex={
                                                                index
                                                            }
                                                        />
                                                    );
                                                }
                                            )}
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </SimpleBar>
            </div>
        </div>
    );
};

const messages = defineMessages({
    title: {
        id: "dashboard-progressionTable-title",
        defaultMessage: "Individual advancements by objectives",
    },
    progressionTableSummary: {
        id: "dashboard-progressionTable-summary",
        defaultMessage: "",
    },
    progressionTableStudentName: {
        id: "dashboard-progressionTable-studentName",
        defaultMessage: "Student's name",
    },
    progressionSeeProfile: {
        id: "dashboard-common-seeProfile",
        defaultMessage: "See profile",
    },
});

export default ProgressionTable;
